import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ─── Design tokens ────────────────────────────────────────────────────────────

export const PAYMENT_HEADER_BG = "#213360";
export const SECTION_BAR_BG = "#1a3a6b";
export const ACCENT = "#19C2B0";
export const ACCENT_DARK = "#13A596";
export const SURFACE = "#F4F7FA";
export const LABEL_COLOR = "#4A5568";
export const BORDER = "#DDE4EC";
export const BRAND = "#0E3F66";
export const BRAND_LIGHT = "#1A5A8A";

// ─── Constants ────────────────────────────────────────────────────────────────

export const yesNoFields = [
  "smoking",
  "drinking",
  "tobacco",
  "hypoglycemia",
  "diabetic_foot",
  "peripheral_neuropathy",
  "peripheral_vascular_disease",
  "diabetes_kidney_disease",
  "diabetic_retinopathy",
  "hypertension",
  "metabolic_syndrome",
];

export const leftRightCheckboxFields = [
  "previous_ulcer",
  "previous_amputation",
  "absent_pedal_pulses",
  "active_ulcer",
  "callus",
  "blisters",
  "fissures",
  "neuropathy",
  "corn",
  "nails_thickened",
  "nails_ingrown",
  "nails_fungal",
  "deformities_claw_toes",
  "deformities_hammer_toes",
  "deformities_hallux_valgus",
  "deformities_flat_foot",
  "deformities_charcot_foot",
  "infection_redness",
  "infection_pus",
  "infection_swelling",
];

export const SelectOptions = {
  exercise: ["Sedentary", "Mild", "Moderate", "Severe"],
  diet: ["Vegetarian", "Vegan", "High Protein", "Pure Vegetarian", "Non Vegetarian"],
  diabetes_type: ["Type 1", "Type 2", "Gestational", "Other"],
  left_foot_risk: ["Low", "Moderate", "High"],
  right_foot_risk: ["Low", "Moderate", "High"],
  skin_hydration: ["Dry & Cracking", "Bacterial", "Fungal", "Diabetic Dermopathy"],
  teeth_status: ["Caries", "Discoloration", "Mobility", "Broken"],
  gums_status: ["Redness", "Swelling", "Bleeding", "Receding", "Tender"],
  cardiovascular_risk: ["Low", "Moderate", "High"],
};

// ─── Shared field styles ──────────────────────────────────────────────────────

export const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: 13,
    transition: "box-shadow 0.2s",
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: BRAND_LIGHT },
    "&.Mui-focused fieldset": { borderColor: ACCENT, borderWidth: "1.5px" },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${ACCENT}22` },
  },
  "& .MuiInputBase-input": { 
    fontSize: 13, 
    py: "10px", 
    lineHeight: 1.5 
  },
  "& .MuiInputBase-inputMultiline": {
    paddingTop: 0,
    paddingBottom: 0,
  },
  "& .MuiSelect-select": { fontSize: 13, py: "8px" },
};

export const labelSx = {
  color: LABEL_COLOR,
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.4,
};

export const rowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "minmax(150px, 185px) 1fr" },
  gap: { xs: 1, sm: 2 },
  alignItems: { xs: "stretch", sm: "center" },
};

// ─── Local Styled Accordions ───────────────────────────────────────────────

const CaseSheetCustomAccordion = styled(MuiAccordion)(({ theme }) => ({
  backgroundColor: "#FFFFFF",
  boxShadow: "none",
  borderRadius: "6px",
  marginBottom: theme.spacing(1),
  border: "1px solid #B1B1B1BB",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: theme.spacing(0.5, 0),
    backgroundColor: "#FFFFFF",
    border: "1px solid #4DD7CD",
  },
}));

const CaseSheetCustomAccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== "hasHeaderContent",
})(({ hasHeaderContent }) => ({
  minHeight: "42px",
  padding: "0 12px",
  "& .MuiAccordionSummary-content": {
    margin: 0,
    width: "100%",
    display: "flex",
    flexDirection: hasHeaderContent ? "column" : "row",
    alignItems: "center",
  },
  "&.Mui-expanded": {
    backgroundColor: "#FFFFFF",
  },
  "& .MuiTypography-root": {
    fontSize: "14px",
    fontWeight: 500,
    color: "#2a3c50",
    textAlign: "left",
  },
}));

const CaseSheetCustomAccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: "12px 16px",
  backgroundColor: "#FFFFFF",
  borderTop: "1px solid #e0e0e0",
  ".Mui-expanded &": {
    borderTop: "none",
  },
}));

export function CaseSheetStyledAccordion({
  title,
  actions,
  headerContent,
  children,
  expanded,
  onChange,
  ...props
}) {
  return (
    <CaseSheetCustomAccordion expanded={expanded} onChange={onChange} {...props}>
      <CaseSheetCustomAccordionSummary
        expandIcon={<ExpandMoreIcon />}
        hasHeaderContent={!!headerContent}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Typography sx={{ flex: 1 }}>{title}</Typography>
          {actions && <Box sx={{ mr: 2 }}>{actions}</Box>}
        </Box>

        {headerContent ? <Box sx={{ width: "97%" }}>{headerContent}</Box> : null}
      </CaseSheetCustomAccordionSummary>

      <CaseSheetCustomAccordionDetails>{children}</CaseSheetCustomAccordionDetails>
    </CaseSheetCustomAccordion>
  );
}

export function CaseSheetSection({ title, defaultExpanded = true, children }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const handleChange = useCallback((_, next) => {
    setExpanded(next);
  }, []);
  return (
    <CaseSheetStyledAccordion title={title} expanded={expanded} onChange={handleChange}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1.75, sm: 2.25 },
        }}
      >
        {children}
      </Box>
    </CaseSheetStyledAccordion>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

export const toTitleCase = (str) =>
  str
    .split("_")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
